/**
 * Firebase Initialization
 *
 * Firebase is used for Google sign-in on the frontend and Analytics.
 * The ID token from Google sign-in is sent to the backend for verification.
 * All other Firebase/Firestore operations are handled server-side
 * by the Rust backend (blinddate).
 *
 * See src/utils/api.js for the backend API client.
 */

import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { getAnalytics } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Only initialize if we have the required config
const hasConfig = firebaseConfig.apiKey && firebaseConfig.apiKey !== 'YOUR_API_KEY';

let app = null;
let auth = null;
let googleProvider = null;
let analytics = null;

if (hasConfig) {
  try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    googleProvider = new GoogleAuthProvider();
    googleProvider.setCustomParameters({
      prompt: 'select_account',
    });
    analytics = getAnalytics(app);
    console.log('✅ Firebase initialized (Auth + Analytics)');
  } catch (err) {
    console.warn('⚠️ Firebase initialization failed:', err.message);
  }
} else {
  console.warn('⚠️ Firebase config not found. Google sign-in disabled. Set REACT_APP_FIREBASE_* env vars.');
}

/**
 * Sign in with Google popup and return the ID token.
 * @returns {Promise<{idToken: string, user: Object}>}
 */
export async function signInWithGoogle() {
  if (!auth || !googleProvider) {
    throw new Error('Firebase Auth not configured. Check your .env file.');
  }

  const result = await signInWithPopup(auth, googleProvider);
  const idToken = await result.user.getIdToken();

  return {
    idToken,
    user: {
      uid: result.user.uid,
      displayName: result.user.displayName,
      email: result.user.email,
      photoURL: result.user.photoURL,
    },
  };
}

/**
 * Check if Firebase Auth is available.
 */
export function isFirebaseAuthAvailable() {
  return auth !== null && googleProvider !== null;
}

export { app, analytics };
export const isLocal = !hasConfig;
